<template>
  <layout>
    <bb-container fluid>
      <div v-if="!representative" class="loader">
        <bb-spinner/>
      </div>
      <div v-if="representative">
        <signing-modal
          ref="signing"
          :initMethod="initSigning"
          @signingSuccessful="onSuccessfulSigning"
          @signingCancelled="onSigningCancelled"
          @signingError="onSigningError"
        />

        <div
          class="d-flex justify-content-between m-t-20">
          <h4 class="f-gotham-medium f-color-navy">{{ translations.title }}</h4>
          <bb-button
            name="download-policies-confirmation"
            :label="translations.downloadDocument"
            iconAfter="ui-download"
            inverted
            color="gray"
            @click="downloadDocument"
            :disabled="isDownloadRunning"
          />
        </div>

        <bb-card size="full" class="m-t-20">
          <h5 class="f-gotham-medium f-color-navy">{{ translations.personalInformation }}</h5>
          <div class="m-t-30">
            <div v-for="([key, value]) in Object.entries(representativeInformation)" :key="key">
              <bb-row class="w-100 m-y-10 no-gutters">
                <bb-col col="12" class="f-gotham-book f-small">{{ translations[key] }}</bb-col>
                <bb-col col="12" class="text-align-right f-gotham-bold">{{ value || '-' }}</bb-col>
              </bb-row>
              <bb-separator v-if="!isLastInArray(Object.keys(representativeInformation), key)" dashed/>
            </div>
          </div>
          <h5 class="f-gotham-medium f-color-navy m-t-30">{{ translations.companyDataAndDocuments }}</h5>
          <div class="m-t-30">
            <div v-for="permission in genericPermissions" v-bind:key="permission">
              <bb-row class="w-100 m-y-10 no-gutters">
                <bb-col col="12" class="f-gotham-book f-small">{{ translations.permissions[permission] }}</bb-col>
                <bb-col col="12" class="text-align-right">
                  <bb-icon
                    name="ui-check"
                    fill="blue"
                    size="24"
                  />
                </bb-col>
              </bb-row>
              <bb-separator dashed v-if="!isLastInArray(genericPermissions, permission)"/>
            </div>
          </div>
          <h5 class="f-gotham-medium f-color-navy m-t-30">{{ translations.addedUserRights }}</h5>
          <bb-box :thin="true" class="m-t-30">
            <h5 class="f-gotham-bold f-small f-color-blue f-case-upper"> {{ translations.superuserRightsGranted }} </h5>
            <p class="f-small m-t-10">{{ translations.superUserPermissionDescription }} </p>
          </bb-box>
          <div class="m-t-20">
            <bb-accordion
              v-for="(permissionGroup) in productPermissionGroups()"
              :key="permissionGroup"
              :value="toAccordionRow(permissionGroup)"
              :flat="true"
              class="bb-accordion"
            >
              <template #[`${permissionGroup}-title`]>
                <h5 class="f-gotham-medium f-color-navy">{{ translations.permissionGroups[permissionGroup] }}</h5>
              </template>
              <template #[`${permissionGroup}`]>
                <div v-for="permission in groupPermissions([permissionGroup])" v-bind:key="permission">
                  <bb-row class="w-100 m-y-10 no-gutters">
                    <bb-col col="12" class="f-gotham-book f-small">{{ translations.permissions[permission] }}</bb-col>
                    <bb-col col="12" class="text-align-right">
                      <bb-icon
                        name="ui-check"
                        fill="blue"
                        size="24"
                      />
                    </bb-col>
                  </bb-row>
                  <bb-separator dashed v-if="!isLastInArray(groupPermissions([permissionGroup]), permission)"/>
                </div>
              </template>
            </bb-accordion>
          </div>
        </bb-card>
        <div
          class="d-flex justify-content-end m-t-20">
          <bb-button @click="initPermissionsSigning" :disabled="confirmButtonDisabled">
            {{ translations.confirmAndSign }}
          </bb-button>
        </div>

      </div>
    </bb-container>
    <permissions-confirmation-information-modal v-if="!hasAcceptedModal" :on-proceed-fn="() => hasAcceptedModal = true"/>
  </layout>
</template>

<script>
import Layout from './Layout'
import accountApi from '@account/api'
import PermissionsConfirmationInformationModal from '@/components/PermissionsConfirmationInformationModal.vue'
import '@bigbank/interface-components/dist/svg/client/pdf'
import '@bigbank/interface-components/dist/svg/ui/download'
import '@bigbank/interface-components/dist/svg/client/upload'
import { BbAccordion, BbButton, BbCard, BbIcon } from '@bigbank/interface-components'
import { mapActions, mapState } from 'pinia'
import { useRootStore } from '@/store/root'
import {
  KnownPolicyPermission,
  PermissionGroup,
  permissionGroups
} from '@bigbank/dc-common/clients/http/access-management/access-management.enums'
import '@bigbank/interface-components/dist/svg/ui/check'
import SigningModal from '@account/components/signing/SigningModal.vue'
import { PERMISSIONS, PREFILL_SOURCE } from '@/TrackingActions'
import { permissionGroupTranslations, permissionTranslations } from '@account/translations/permissionTranslations'
import { formatDate, getDateFormat } from '@/plugins/dateFormatters'
import dayjs from 'dayjs'

const genericPermissionGroups = [PermissionGroup.CustomerData]
const hiddenPermissionGroups = [PermissionGroup.Login, PermissionGroup.Admin]

export default {
  name: 'permissions-confirmation',
  components: { PermissionsConfirmationInformationModal, BbAccordion, SigningModal, Layout, BbCard, BbIcon, BbButton },
  data () {
    return {
      confirmButtonDisabled: false,
      isDownloadRunning: false,
      hasAcceptedModal: false,
      representative: undefined
    }
  },
  computed: {
    ...mapState(useRootStore, ['company']),
    validityPeriod () {
      const effectiveFrom = dayjs(this.representative.effectiveFrom).format(getDateFormat())
      const effectiveTo = this.representative.effectiveTo ? dayjs(this.representative.effectiveTo).format(getDateFormat()) : '...'
      return effectiveFrom + ' - ' + effectiveTo
    },
    genericPermissions () {
      return this.groupPermissions(genericPermissionGroups)
    },
    translations () {
      return {
        title: this.$pgettext('permissions_confirmation', 'Summary'),
        downloadDocument: this.$pgettext('permissions_confirmation', 'Download document'),
        personalInformation: this.$pgettext('permissions_confirmation', 'Personal information'),
        companyDataAndDocuments: this.$pgettext('permissions_confirmation', 'Company data and documents'),
        addedUserRights: this.$pgettext('permissions_confirmation', 'Added user rights'),
        superuserRightsGranted: this.$pgettext('permissions_confirmation', 'Superuser rights granted'),
        superUserPermissionDescription: this.$pgettext('permissions_confirmation', 'You now have the authority to manage access to your company’s services in the internet bank. This includes adding new users and deciding the level of access each person should have.'),
        fullName: this.$pgettext('permissions_confirmation', 'First and last name'),
        identificationCode: this.$pgettext('permissions_confirmation', 'ID code'),
        phoneNumber: this.$pgettext('permissions_confirmation', 'Phone number'),
        email: this.$pgettext('permissions_confirmation', 'E-mail'),
        validityPeriod: this.$pgettext('permissions_confirmation', 'Validity'),
        confirmAndSign: this.$pgettext('confirm_and_sign', 'Confirm and sign'),
        permissions: permissionTranslations(),
        permissionGroups: permissionGroupTranslations()
      }
    },
    representativeInformation () {
      return {
        fullName: this.representative.fullName,
        identificationCode: this.representative.identificationCode,
        phoneNumber: this.representative.phoneNumber,
        email: this.representative.email,
        validityPeriod: this.validityPeriod
      }
    }
  },
  methods: {
    ...mapActions(useRootStore, ['setPermissionsConfirmed', 'checkAuthentication']),
    getDateFormat,
    formatDate,
    productPermissionGroups () {
      return Object.values(PermissionGroup)
        .filter(group => !genericPermissionGroups.includes(group) && !hiddenPermissionGroups.includes(group))
    },
    groupPermissions (groups) {
      return groups.flatMap(group => permissionGroups[group])
    },
    async initPermissionsSigning (e) {
      await this.$refs.signing.signButtonClick(e)
    },
    async downloadDocument () {
      this.isDownloadRunning = true
      await accountApi.downloadPermissionConfirmation()
      this.isDownloadRunning = false
    },
    async initSigning () {
      try {
        this.confirmButtonDisabled = true
        const response = await accountApi.initPermissionsConfirmation({ permissions: Object.values(KnownPolicyPermission) })
        this.$tracker.action(PERMISSIONS.SIGNING_INITIATED, { prefillSource: PREFILL_SOURCE.NONE })
        return response
      } catch (e) {
        this.confirmButtonDisabled = false
        return e.data.signingMethod ? { method: e.data.signingMethod } : null
      }
    },
    async onSuccessfulSigning () {
      this.setPermissionsConfirmed()
      await this.checkAuthentication()
      this.$notify({
        title: this.$pgettext('permissions_confirmation', 'Permissions confirmed successfully'),
        text: this.$pgettext('permissions_confirmation', 'You can now proceed to use the internet bank'),
        type: 'success',
        duration: 10000
      })
      const redirectTo = this.$route.query.redirectTo || '/account/users'
      await this.$router.push({ path: redirectTo })
    },
    async onSigningCancelled () {
      this.confirmButtonDisabled = false
    },
    async onSigningError (e) {
      this.confirmButtonDisabled = false
      this.$notify({
        title: this.$pgettext('error', 'Something went wrong'),
        text: this.$pgettext('error', 'Please try again later'),
        type: 'error',
        duration: 10000
      })
    },
    isLastInArray (array, element) {
      return array.indexOf(element) === array.length - 1
    },
    toAccordionRow (permissionGroup) {
      return [{
        id: permissionGroup,
        title: this.translations.permissionGroups[permissionGroup],
        open: false
      }]
    }
  },
  async mounted () {
    this.representative = await accountApi.getRepresentative()
  }
}
</script>
<style lang="scss" scoped>
.text-align-right {
  text-align: right;
  color: $blue;
}

.bb-accordion {
  background: $gray-10;
}

.bb-accordion:not(:last-child) {
  margin-bottom: rem(15px);
}
</style>
